import type { AbstractPartDefinition } from "@project/emerge-types";
import * as THREE from "three";
import { Alignment } from "../enum/Alignment";
import type { ConnectorSetInfos } from "../interfaces/ConnectorSetInfos";

/** Min/max margin (mm) along one connector’s edge drag segment — same semantics as {@link MarginAxisRange} in addableMarginDragRange. */
export interface ConnectorMarginAxisRange {
  min: number;
  max: number;
}

function isCenterAlign(a: unknown): boolean {
  return a === Alignment.CENTER || a === 1 || a === "CENTER";
}

function isEndAlign(a: unknown): boolean {
  return a === Alignment.END || a === 2 || a === "END";
}

/**
 * Same margin math as `ProfileBuildStep.updateSettingsFromConnectorDrag` (before `Math.round`).
 */
export function marginFromConnectorWorldPosition(
  newPosition: THREE.Vector3,
  params: {
    originalEdgeStart: THREE.Vector3;
    originalEdgeEnd: THREE.Vector3;
    originalEdgeLength: number;
    edgeDir: THREE.Vector3;
    count: number;
    n: number;
    padding: number;
    alignment: unknown;
  },
): number | null {
  const {
    originalEdgeStart,
    originalEdgeEnd,
    originalEdgeLength,
    edgeDir,
    count,
    n,
    padding,
    alignment,
  } = params;

  const distFromStart = newPosition.clone().sub(originalEdgeStart).dot(edgeDir);
  const distFromEnd = originalEdgeLength - distFromStart;

  if (count > 1) {
    const L = originalEdgeLength;
    const denom = 1 - 2 * n;
    if (Math.abs(denom) < 0.001) {
      return null;
    }
    let newMargin = (distFromStart - L * n - padding * denom) / denom;
    newMargin = Math.max(0, newMargin);
    return newMargin;
  }
  if (isCenterAlign(alignment)) {
    const originalCenter = originalEdgeStart
      .clone()
      .add(originalEdgeEnd)
      .multiplyScalar(0.5);
    const distFromCenterVec = newPosition.clone().sub(originalCenter);
    return distFromCenterVec.dot(edgeDir);
  }
  if (isEndAlign(alignment)) {
    return distFromEnd - padding;
  }
  return distFromStart - padding;
}

/**
 * Margin range implied by edge drag bounds (padded segment endpoints) for one 3D part.
 */
export function computeConnectorMarginRangeForEdgePart(
  part: AbstractPartDefinition<string>,
  option: Pick<ConnectorSetInfos, "alignment" | "count" | "partSelection">,
): ConnectorMarginAxisRange | null {
  const constraint = part.dragConstraint;
  const ctx = part.settingsContext;
  if (!constraint || constraint.type !== "edge" || !ctx) {
    return null;
  }

  const { min: bMin, max: bMax } = constraint.bounds;
  const p0 = new THREE.Vector3(bMin[0], bMin[1], bMin[2]);
  const p1 = new THREE.Vector3(bMax[0], bMax[1], bMax[2]);

  const originalEdgeStart = new THREE.Vector3(
    ...(ctx.originalEdgeStartPos as [number, number, number]),
  );
  const originalEdgeEnd = new THREE.Vector3(
    ...(ctx.originalEdgeEndPos as [number, number, number]),
  );
  const edgeDir = new THREE.Vector3(
    ...(ctx.edgeDirection as [number, number, number]),
  );
  const originalEdgeLength = ctx.originalEdgeLength as number;
  const n = (ctx.n as number) ?? 0;
  const count = option.count;
  const partSelection = option.partSelection as { cut?: number } | undefined;
  const padding = (partSelection?.cut ?? 0) / 2;

  const baseParams = {
    originalEdgeStart,
    originalEdgeEnd,
    originalEdgeLength,
    edgeDir,
    count,
    n,
    padding,
    alignment: option.alignment,
  };

  const m0 = marginFromConnectorWorldPosition(p0, baseParams);
  const m1 = marginFromConnectorWorldPosition(p1, baseParams);
  if (m0 === null || m1 === null) {
    return null;
  }
  return {
    min: Math.min(m0, m1),
    max: Math.max(m0, m1),
  };
}

/** Intersection of per-part ranges (same shared `margin` must satisfy every draggable instance). */
export function intersectConnectorMarginRanges(
  ranges: readonly ConnectorMarginAxisRange[],
): ConnectorMarginAxisRange | null {
  if (ranges.length === 0) {
    return null;
  }
  const first = ranges[0];
  if (!first) {
    return null;
  }
  let lo = first.min;
  let hi = first.max;
  for (let i = 1; i < ranges.length; i++) {
    const r = ranges[i];
    if (!r) continue;
    lo = Math.max(lo, r.min);
    hi = Math.min(hi, r.max);
  }
  if (lo > hi) {
    return null;
  }
  return { min: lo, max: hi };
}
